import type { TippyConfig } from '@/core/features/tooltip/tooltip';
import type { ISpotScore } from '@/features/spot-score/spot-score-type';

import React from 'react';

import { HtmlTextWrapper, Tooltip } from '@/core/features';
import { useRefRerender } from '@/core/features/render/use-ref-rerender';
import SpotScoreOverview from '@/features/spot-score/spot-score-overview';

interface SpotScoreBadgeDesktopProps {
    appendTo?: TippyConfig['appendTo'];
    flip?: boolean;
    fontWeight?: 'bold' | 'normal';
    hideScoreDescription?: boolean;
    onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
    placement: 'bottom-start' | 'left-start';
    qaId?: string;
    scoreMarginBottom?: number;
    size?: 32 | 40;
    spotScore: ISpotScore;
    type?: 'horizontal' | 'horizontalReverse' | 'vertical';
}

export default function SpotScoreBadgeDesktop({
    appendTo,
    flip = true,
    fontWeight = 'normal',
    hideScoreDescription,
    onClick,
    placement,
    qaId,
    scoreMarginBottom,
    size = 40,
    spotScore,
    type = 'horizontal',
}: SpotScoreBadgeDesktopProps) {
    const tooltipTriggerTargetRef = React.useRef<HTMLDivElement>(null);

    useRefRerender(tooltipTriggerTargetRef);

    return (
        <div
            className={`flex shrink-0 items-center cursor-default ${type === 'horizontalReverse' ? 'justify-end' : ''}`}
            onClick={onClick}
            ref={tooltipTriggerTargetRef}
            style={{
                flex: type !== 'vertical' ? 'none' : undefined,
                flexDirection:
                    type === 'vertical' ? 'column-reverse' : type === 'horizontalReverse' ? 'row-reverse' : 'row',
                gap: type !== 'vertical' ? 10 : undefined,
                marginBottom: scoreMarginBottom ? `${scoreMarginBottom}px` : undefined,
            }}
        >
            {!hideScoreDescription && spotScore.htmlScoreDescription && (
                <span className={`text-14 ${fontWeight === 'bold' ? 'font-bold' : 'font-normal'}`}>
                    <HtmlTextWrapper htmlText={spotScore.htmlScoreDescription} />
                </span>
            )}
            <div
                className={'flex justify-center items-center text-14 text-white bg-check24-blue rounded-5'}
                data-qa-id={qaId}
                style={{
                    // eslint-disable-next-line no-inline-styles/no-inline-styles
                    borderBottomRightRadius: 0,
                    height: size,
                    width: size,
                }}
            >
                <SpotScoreBadgeTooltip
                    appendTo={appendTo}
                    flip={flip}
                    placement={placement}
                    spotScore={spotScore}
                    triggerTarget={tooltipTriggerTargetRef}
                />
            </div>
        </div>
    );
}

const SpotScoreBadgeTooltip = ({
    appendTo,
    flip = true,
    placement,
    spotScore,
    triggerTarget,
}: {
    appendTo?: TippyConfig['appendTo'];
    flip?: boolean;
    placement: 'bottom-start' | 'left-start';
    spotScore: ISpotScore;
    triggerTarget: React.RefObject<HTMLDivElement>;
}) => {
    const [isVisible, setIsVisible] = React.useState(false);
    if (spotScore.details) {
        return (
            <Tooltip
                ariaLabel={`spotScore-${spotScore.scoreText}`}
                content={
                    <div className={'p-20'}>
                        <SpotScoreOverview
                            isVisible={isVisible}
                            spotScoreDetails={spotScore.details}
                            spotScoreText={spotScore.scoreText}
                        />
                    </div>
                }
                flip={flip}
                tippyConfig={{
                    appendTo,
                    maxWidth: 385,
                    offset: [0, 20],
                    onHide: () => setIsVisible(false),
                    onShow: () => setIsVisible(true),
                    placement: placement,
                    triggerTarget: triggerTarget.current,
                }}
            >
                <span className={'text-white font-bold'}>{spotScore.scoreText}</span>
            </Tooltip>
        );
    }

    return (
        <Tooltip
            ariaLabel={`spotScore-${spotScore.scoreText}`}
            content={<div className={'p-15 text-center'}>Dieser Spot wurde noch nicht bewertet.</div>}
            tippyConfig={{
                appendTo,
                offset: [0, 20],
                placement: 'bottom',
                triggerTarget: triggerTarget.current,
            }}
        >
            <span className={'text-white font-bold'}>{spotScore.scoreText}</span>
        </Tooltip>
    );
};
